import {
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { Button, Input } from '@leadsales/design-system/react';
import { KeyboardEvent, ReactNode, useEffect, useId, useRef } from 'react';
import { Loading } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { integrationNames } from 'src/constants/channel.constants';
import { MIN_SEARCH_RUNES } from '../../../../pages/MessengerV2/MessageSearch.constants';
import { FOCUS_DISMISS_EXEMPT_ATTRIBUTE } from '../MessageContainer/MessageContainer.focus';
import { useChatSearchContext } from './ChatSearch.context';
import { LEAD_SEARCH_WINDOW_MONTHS } from './ChatSearch.functions';
import { tChatSearchContext, tChatSearchStatus } from './ChatSearch.types';
import useTrailingInset from './ChatSearchBarLayout.hook';
import useChatSearchBarMotion, {
  tChatSearchBarMotionRefs,
} from './ChatSearchBarMotion.hook';
import useDeferredProgress from './ChatSearchProgress.hook';
import './ChatSearchBar.styles.scss';

const noop = () => undefined;

/** Longest query the field accepts, the same cap as the inbox search box. */
const MAX_QUERY_LENGTH = 255;

/** The states whose counter is an answer about a search, worth keeping. */
const OUTCOMES: tChatSearchStatus[] = ['results', 'empty', 'error'];

type tChatSearchBarProps = {
  /** "Cerrar" closed the search and the bar is going back into the
   *  magnifier: it shows what it showed, and nothing in it responds. */
  leaving?: boolean;
  /** The way out has played (ChatSearchPresence unmounts the bar). */
  onLeft?: () => void;
};

/** What the bar shows; frozen while it leaves, because the search it shows
 *  has already been cleared by then. */
type tBarView = {
  inputValue: string;
  count: ReactNode;
  status: tChatSearchStatus;
  canGoOlder: boolean;
  canGoNewer: boolean;
};

type tCountProps = {
  search: tChatSearchContext;
  t: (key: string, options?: Record<string, unknown>) => string;
};

/**
 * The counter inside the field, right of the text: one line, one node.
 *
 * There is no total in the contract, so "de N" is what is loaded: "1 de 2"
 * once the search is complete, "1 de 8+" while older pages remain. A page the
 * server stopped at its budget says "No hay resultados recientes" and offers
 * "Seguir buscando", never "no results". At the last loaded match ∧ already
 * fetches the next page (resolveOlderStep), so the results states offer no
 * button of their own.
 *
 * The wait ("Buscando…") is not here: the bar shows it only past 400 ms.
 *
 * @returns The counter, or null when there is nothing to say (idle, waiting).
 */
function describeCount(props: tCountProps): ReactNode {
  const { search, t } = props;
  const loaded = search.matches.length;

  const text = (copy: string, variant?: 'muted' | 'error') => (
    <span
      className={
        variant
          ? `ChatSearchBar__CountText ChatSearchBar__CountText--${variant}`
          : 'ChatSearchBar__CountText'
      }
    >
      {copy}
    </span>
  );
  const link = (copy: string, testId: string, onClick: () => void) => (
    <Button
      color="ghost"
      size="sm"
      className="ls-btn-link ChatSearchBar__Link"
      data-testid={testId}
      onClick={onClick}
    >
      {copy}
    </Button>
  );
  const failed = (
    <>
      {text(t('chatSearch.error'), 'error')}
      {link(t('chatSearch.retry'), 'chat-search-retry', search.retry)}
    </>
  );

  switch (search.status) {
    case 'idle':
    case 'loading':
      return null;
    case 'hint':
      return text(t('chatSearch.hint', { min: MIN_SEARCH_RUNES }), 'muted');
    case 'negation':
      return text(t('chatSearch.negation'), 'muted');
    case 'error':
      return failed;
    case 'empty':
      if (search.loadMoreFailed) return failed;
      if (search.hasMore)
        return (
          <>
            {text(t('chatSearch.noMatchesYet'), 'muted')}
            {link(
              t('chatSearch.keepSearching'),
              'chat-search-continue',
              search.continueSearch,
            )}
          </>
        );
      return text(
        t('chatSearch.noResultsWindow', { months: LEAD_SEARCH_WINDOW_MONTHS }),
        'muted',
      );
    default:
      break;
  }

  if (search.loadMoreFailed) return failed;

  const position = {
    position: Math.max(search.activeIndex, 0) + 1,
    total: loaded,
  };

  if (search.channelNotice)
    return text(
      t(
        search.hasMore
          ? 'chatSearch.positionInChannelMore'
          : 'chatSearch.positionInChannel',
        {
          ...position,
          channel:
            integrationNames[search.channelNotice] ?? search.channelNotice,
        },
      ),
    );

  return text(
    t(
      search.hasMore ? 'chatSearch.positionOfMore' : 'chatSearch.positionOf',
      position,
    ),
  );
}

/**
 * "Buscar en esta conversación" (SRE-3328), laid over ChatMenu's row. One row:
 * [field: magnifier · text · counter · divider · ∧ · ∨] [Cerrar ×].
 * Presentation only: every decision lives in useChatSearch.
 *
 * Keyboard: Enter goes to the older match, Shift+Enter to the newer one, Esc
 * closes at once. Esc stops here: the reply mode (EventBubble) and the
 * Pipe/Directory chat modals listen for Esc on `document`, and closing the
 * search must not also cancel a reply or close the modal around it.
 *
 * Opened from the magnifier it grows out of it, and "Cerrar" sends it back
 * there (useChatSearchBarMotion; DES-TBD, an approved exception to the DS
 * reveal pattern, see ChatSearch.motion). Ctrl/⌘+F and Esc are instant.
 *
 * The root carries FOCUS_DISMISS_EXEMPT_ATTRIBUTE, so typing and stepping here
 * never take the jump highlight off the match on screen.
 */
export default function ChatSearchBar(props: tChatSearchBarProps) {
  const { leaving = false, onLeft = noop } = props;
  const search = useChatSearchContext();
  const { t } = useTranslation('chat/chat');
  const scopeId = useId();
  const countId = useId();
  const refs: tChatSearchBarMotionRefs = {
    root: useRef<HTMLDivElement>(null),
    field: useRef<HTMLDivElement>(null),
    icon: useRef<HTMLSpanElement>(null),
    growStart: useRef<HTMLSpanElement>(null),
    growBody: useRef<HTMLSpanElement>(null),
    growEnd: useRef<HTMLSpanElement>(null),
    trailing: useRef<HTMLDivElement>(null),
    close: useRef<HTMLButtonElement>(null),
  };
  const lastOutcomeRef = useRef<ReactNode>(null);
  const shownRef = useRef<tBarView | null>(null);

  const status = search?.status ?? 'idle';
  const busy = status === 'loading' || Boolean(search?.loadingMore);
  const showProgress = useDeferredProgress(busy);
  const enterMode = useChatSearchBarMotion({ search, leaving, onLeft, refs });

  useTrailingInset(refs.field, refs.trailing);

  const settled = search && !busy ? describeCount({ search, t }) : null;
  const loaded = search?.matches.length ?? 0;
  const activeIndex = search?.activeIndex ?? -1;
  const atLast = activeIndex === loaded - 1;
  const stepping = status === 'results' || status === 'empty';

  let count: ReactNode = settled;

  if (showProgress)
    count = (
      <span className="ChatSearchBar__CountText ChatSearchBar__CountText--muted ChatSearchBar__Progress">
        <Loading size="xs" aria-hidden="true" />
        {t('chatSearch.loading')}
      </span>
    );
  else if (busy) count = lastOutcomeRef.current;

  const live: tBarView = {
    inputValue: search?.inputValue ?? '',
    count,
    status,
    canGoOlder:
      stepping && !search?.loadingMore && (!atLast || Boolean(search?.hasMore)),
    canGoNewer: status === 'results' && !search?.loadingMore && activeIndex > 0,
  };
  const view = leaving && shownRef.current ? shownRef.current : live;

  useEffect(() => {
    if (leaving) return;

    shownRef.current = live;
    // Under 400 ms of waiting the counter keeps the last answer it gave about
    // a search, instead of blanking; a hint about the text being typed is not
    // one.
    if (!busy)
      lastOutcomeRef.current = OUTCOMES.includes(status) ? settled : null;
  });

  if (!search) return null;

  function handleRootKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Escape' || !search) return;

    event.preventDefault();
    event.stopPropagation();
    search.close();
  }

  function handleInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter' || !search) return;

    event.preventDefault();

    if (event.shiftKey) search.newer();
    else search.older();
  }

  // React 18 has no `inert` prop: it goes as an attribute (React 19 takes a
  // boolean).
  const leavingProps = leaving ? { inert: '', 'data-leaving': '' } : {};

  return (
    <div
      ref={refs.root}
      className="ChatSearchBar"
      role="search"
      data-testid="chat-search-bar"
      data-motion={enterMode}
      {...{ [FOCUS_DISMISS_EXEMPT_ATTRIBUTE]: '' }}
      {...leavingProps}
      onKeyDown={handleRootKeyDown}
    >
      <div ref={refs.field} className="ChatSearchBar__Field">
        <div className="ChatSearchBar__Grow" aria-hidden="true">
          <span
            ref={refs.growStart}
            className="ChatSearchBar__GrowCap ChatSearchBar__GrowCap--start"
          />
          <span ref={refs.growBody} className="ChatSearchBar__GrowBody" />
          <span
            ref={refs.growEnd}
            className="ChatSearchBar__GrowCap ChatSearchBar__GrowCap--end"
          />
        </div>

        <Input
          ref={search.inputRef}
          size="sm"
          autoFocus
          readOnly={leaving}
          type="text"
          autoComplete="off"
          spellCheck={false}
          maxLength={MAX_QUERY_LENGTH}
          placeholder={t('chatSearch.placeholder')}
          aria-label={t('chatSearch.placeholder')}
          aria-describedby={`${scopeId} ${countId}`}
          data-testid="chat-search-input"
          className="ChatSearchBar__Input"
          wrapperClassName="ChatSearchBar__InputWrapper"
          leadingIcon={
            <span ref={refs.icon} className="ChatSearchBar__LeadingIcon">
              <MagnifyingGlassIcon />
            </span>
          }
          value={view.inputValue}
          onChange={(event) => search.setInputValue(event.target.value)}
          onKeyDown={handleInputKeyDown}
        />

        <div ref={refs.trailing} className="ChatSearchBar__Trailing">
          <div
            id={countId}
            className="ChatSearchBar__Count"
            aria-live="polite"
            data-testid="chat-search-count"
          >
            {view.count}
          </div>

          {view.status === 'idle' ? null : (
            <>
              <span className="ChatSearchBar__Divider" aria-hidden="true" />
              <Button
                color="ghost"
                size="sm"
                iconOnly
                className="ChatSearchBar__NavButton"
                aria-label={t('chatSearch.older')}
                title={t('chatSearch.olderTitle')}
                data-testid="chat-search-older"
                disabled={!view.canGoOlder}
                onClick={search.older}
              >
                <ChevronUpIcon />
              </Button>
              <Button
                color="ghost"
                size="sm"
                iconOnly
                className="ChatSearchBar__NavButton"
                aria-label={t('chatSearch.newer')}
                title={t('chatSearch.newerTitle')}
                data-testid="chat-search-newer"
                disabled={!view.canGoNewer}
                onClick={search.newer}
              >
                <ChevronDownIcon />
              </Button>
            </>
          )}
        </div>
      </div>

      <Button
        ref={refs.close}
        color="ghost"
        size="sm"
        className="ChatSearchBar__Close"
        aria-label={t('chatSearch.close')}
        title={t('chatSearch.closeTitle')}
        data-testid="chat-search-close"
        onClick={() => search.close({ animate: true })}
      >
        <span>{t('chatSearch.closeButton')}</span>
        <XMarkIcon />
      </Button>

      <span id={scopeId} className="sr-only">
        {t('chatSearch.scope', { months: LEAD_SEARCH_WINDOW_MONTHS })}
      </span>
    </div>
  );
}
